import {
  axios,
  checkForValidationError,
  ServerError,
  TServerValidationError,
} from '@/api/common'
import { ItemBomWithComponentsModel } from '@/api/settings/item/get.schema'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import {
  ItemBomCreateModel,
  ItemBomUpdateModel,
  ItemCreateModel,
  ItemUpdateModel,
  ItemUpdateSchema,
} from './mutation.schema'

export async function createItem(data: ItemCreateModel): Promise<void> {
  await axios.post('/api/masterdata/items', data)
}

export const useCreateItemMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['items', 'create'],
    mutationFn: createItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

export async function updateItem(data: ItemUpdateModel): Promise<void> {
  const patch = ItemUpdateSchema.parse(data)
  await axios.put(`/api/masterdata/items/${data.id}`, patch)
}

export const useUpdateItemMutation = (id: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['items', 'update', id],
    mutationFn: updateItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

export async function createItemBom(
  itemId: number,
  data: ItemBomCreateModel
): Promise<void> {
  try {
    await axios.post(`/api/masterdata/items/${itemId}/boms`, data)
  } catch (error) {
    checkForValidationError(error as AxiosError<TServerValidationError>)
    throw new ServerError((error as AxiosError<string>).response?.data)
  }
}

export const useCreateItemBomMutation = (itemId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['items', itemId, 'createBom'],
    mutationFn: (data: ItemBomCreateModel) => createItemBom(itemId, data),
    onSuccess: () => {
      // Invalidate the parent item query (includes BOMs list)
      queryClient.invalidateQueries({ queryKey: ['items', itemId] })
      // Invalidate general items list queries
      queryClient.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

export function initialUpdateItemBomValues(
  data: ItemBomWithComponentsModel
): ItemBomUpdateModel {
  const formData: ItemBomUpdateModel = {
    isDefault: data.isDefault,
    items:
      data.components.length === 0
        ? [
            {
              seq: 1,
              itemId: '',
              qty: 0,
            },
          ]
        : data.components.map((item, i) => {
            return {
              seq: i + 1,
              itemId: `${item.itemId}`,
              qty: item.qty,
            }
          }),
  }
  return formData
}

export async function updateItemBom(
  itemId: number,
  bomId: number,
  data: ItemBomUpdateModel
): Promise<void> {
  try {
    await axios.put(`/api/masterdata/items/${itemId}/boms/${bomId}`, data)
  } catch (error) {
    checkForValidationError(error as AxiosError<TServerValidationError>)
    throw new ServerError((error as AxiosError<string>).response?.data)
  }
}

export const useUpdateItemBomMutation = (itemId: number, bomId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['items', itemId, 'updateBom', bomId],
    mutationFn: (data: ItemBomUpdateModel) =>
      updateItemBom(itemId, bomId, data),
    onSuccess: () => {
      // Invalidate the specific BOM query to ensure fresh data on next edit
      queryClient.invalidateQueries({
        queryKey: ['items', itemId, 'boms', bomId],
      })
      // Invalidate the parent item query (includes BOMs list)
      queryClient.invalidateQueries({ queryKey: ['items', itemId] })
      // Invalidate general items list queries
      queryClient.invalidateQueries({ queryKey: ['items'] })
      // Invalidate related wo3010 cache (for production BOM views)
      queryClient.invalidateQueries({ queryKey: ['wo3010', bomId] })
    },
  })
}
